import { useMemo } from 'react';
import { PolyData } from 'react-vtk-js';
import { distance2BetweenPoints } from '@kitware/vtk.js/Common/Core/Math';
import type { Vector3 } from '@kitware/vtk.js/types';
import * as config from '../config';
import { hexToVTKColor } from '../utils/colors';
import { pointsToDashedLine } from '../utils/points';
import type { Segment, MouseHandlers } from '../types';
import { withCommonPrimitiveHelpers, transformCoordinate, numberToOpacity } from './helpers/common';
import { DisplayLocation } from '@kitware/vtk.js/Rendering/Core/Property2D/Constants';
import BatchedGeometry2DRepresentation from './helpers/BatchedGeometry2DRepresentation';
import type { BasePrimitiveProps } from './PrimitiveTypes';
import { useAnnotationColors } from '../Annotations/helpers/useAnnotationColors';

type DashedLineProps = BasePrimitiveProps & {
  start: Vector3;
  end: Vector3;
  /** World length of each dash and each gap. */
  dashLength?: number;
  /** False for a line that is only drawn, so it never takes the mouse from what is under it. */
  pickable?: boolean;
};

export const DashedLine: React.ComponentType<DashedLineProps & MouseHandlers> =
  withCommonPrimitiveHelpers(
    ({
      id,
      start,
      end,
      color,
      opacity = 1,
      dashLength = 4,
      thickness = config.thickness,
      visible = true,
      pickable = true,
    }: DashedLineProps): React.ReactElement => {
      const { defaultColor } = useAnnotationColors();

      const property = useMemo(
        () => ({
          color: hexToVTKColor(color ?? defaultColor),
          opacity: numberToOpacity(opacity),
          displayLocation: DisplayLocation.FOREGROUND,
          lineWidth: thickness,
        }),
        [color, defaultColor, thickness, opacity]
      );

      const points: Array<number> = useMemo(() => {
        const [startX, startY, startZ] = start;
        const [endX, endY, endZ] = end;
        const x = endX - startX;
        const y = endY - startY;
        const z = endZ - startZ;

        // Number of dashes, they must be odd in order to pick the last point
        let length = Math.floor(Math.sqrt(distance2BetweenPoints(start, end)) / dashLength);
        length = length + ((length % 2) + 1);
        length = length === 1 ? 3 : length;

        // @ts-expect-error [EN-7967] - TS2322 - Type 'Vector3[][]' is not assignable to type 'Segment[]'.
        const dashes: Array<Segment> = [
          ...Array.from({ length }).map(
            (_, i) =>
              [
                startX + (x * i) / length,
                startY + (y * i) / length,
                startZ + (z * i) / length,
              ] as Vector3
          ),
          end,
        ]
          .map((point, i, points) => [point, points[(i + 1) % points.length]])
          .filter((_, i) => i % 2 === 0);
        return dashes.flatMap((dash) => dash.flat());
      }, [start, end, dashLength]);

      const lines = useMemo(() => pointsToDashedLine(points), [points]);
      const actorProperties = useMemo(
        () => ({
          visibility: visible,
          pickable,
        }),
        [visible, pickable]
      );

      return (
        <BatchedGeometry2DRepresentation
          id={id}
          // @ts-expect-error [EN-7967] - TS2559 - Type '{ visibility: boolean; }' has no properties in common with type 'Partial<IActor2DInitialValues>'.
          actor={actorProperties}
          property={property}
          transformCoordinate={transformCoordinate}
        >
          <PolyData points={points} lines={lines} />
        </BatchedGeometry2DRepresentation>
      );
    }
  );
